import { IOperationResult, ISendEmailResponse, ITemplate } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { v4 as uuidgen } from 'uuid';
import { isIE11 } from '../../../models/Browser';
import { Button } from '../../components-v2/Button';
import { baseStyleSheet } from '../../styles/styles';
import { TestEmailModal } from '../email/TestEmailModal';
import { HtmlNewsletterPreview } from '../HtmlNewsletterPreview';
import { Input } from '../../components-v2/Input';
import { EditItemPenIcon } from '../svgs/icons/EditItemPenIcon';
import { styleSheet } from './styles';

interface IHtmlNewsletterCampaignComposerProps {
	className?: string;
	hideSubject?: boolean;
	isForAutomation?: boolean;
	onEditEmailClicked?(e: React.MouseEvent<HTMLElement>): void;
	onSendTestEmailClicked?: (selectedUserId: string) => Promise<IOperationResult<ISendEmailResponse>>;
	onSubjectChanged?(subject: string): void;
	readonly?: boolean;
	styles?: StyleDeclarationValue[];
	subject?: string;
	template: ITemplate;
}

const HtmlNewsletterCampaignComposer: React.FC<IHtmlNewsletterCampaignComposerProps> = props => {
	const {
		className,
		styles,
		onSubjectChanged,
		onSendTestEmailClicked,
		subject,
		template,
		onEditEmailClicked,
		hideSubject,
		isForAutomation,
		readonly,
	} = props;
	const inputGuid = React.useRef(uuidgen()).current;
	const previewerStyles = React.useRef<StyleDeclarationValue[]>([
		styleSheet.htmlNewsletterComposerContent,

		hideSubject ? styleSheet.htmlNewsletterComposerContentWithoutSubject : null,
	]);
	const [internalSubject, setInternalSubject] = React.useState('');
	const [isTestEmailModalOpen, setIsTestEmailModalOpen] = React.useState(false);
	const onChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		if (!!onSubjectChanged && (subject !== null || subject !== undefined)) {
			onSubjectChanged(value);
		} else {
			setInternalSubject(value);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const label = (
		<span>
			Subject:
			<span className={css(baseStyleSheet.required)}>*</span>
			&nbsp;
		</span>
	);

	return (
		<div
			className={`${css(isForAutomation ? styleSheet.htmlNewsletterComposerContentForAutomation : styleSheet.htmlNewsletterComposer, ...(styles || []))}  ${className || ''}`}
		>
			{!hideSubject && (
				<div className={css(styleSheet.htmlNewsletterComposerSubjectHeader)}>
					<div className={css(styleSheet.subjectInputRow)}>
						{label}
						<Input
							disabled={readonly}
							id={`html-newslettter-composer-subject-${inputGuid}`}
							onChange={onChange}
							type='text'
							value={subject || internalSubject || ''}
						/>
					</div>
					{!readonly && !isIE11() && !isForAutomation && (
						<button
							className={css(
								baseStyleSheet.brandLink,
								baseStyleSheet.horizontalStack,
								styleSheet.htmlNewsletterComposerEditButton
							)}
							onClick={onEditEmailClicked}
						>
							<EditItemPenIcon />
							<span>Edit Email</span>
						</button>
					)}
					{onSendTestEmailClicked && (
						<Button variant='secondary' size='sm' onClick={() => setIsTestEmailModalOpen(true)}>
							Send a Test
						</Button>
					)}
				</div>
			)}
			<HtmlNewsletterPreview styles={previewerStyles.current} template={template} />
			{onSendTestEmailClicked && (
				<TestEmailModal
					isOpen={isTestEmailModalOpen}
					onRequestClose={() => setIsTestEmailModalOpen(false)}
					onSendTestEmail={onSendTestEmailClicked}
				/>
			)}
		</div>
	);
};

// eslint-disable-next-line import/no-default-export
export default HtmlNewsletterCampaignComposer;
